/**
 * Newsletter Overlay Web Component
 *
 * Features:
 * - 2 views: form, success
 * - Client-side email validation
 * - Button disabled until valid email + consent checked
 * - Klaviyo integration with graceful fallback
 * - Listens for newsletter-trigger action from footer
 */

if (!customElements.get('newsletter-overlay')) {
  const KLAVIYO_SUBSCRIPTIONS_URL = 'https://a.klaviyo.com/client/subscriptions/';
  const KLAVIYO_API_REVISION = '2026-07-15';

  customElements.define(
    'newsletter-overlay',
    class NewsletterOverlay extends HTMLElement {
      constructor() {
        super();
        this.form = null;
        this.emailInput = null;
        this.consentCheckbox = null;
        this.submitButton = null;
        this.emailField = null;
        this.errorElement = null;
      }

      connectedCallback() {
        this.cacheElements();
        this.bindEvents();
      }

      cacheElements() {
        this.form = this.querySelector('[data-form="newsletter"]');
        this.emailInput = this.querySelector('#NewsletterOverlay-email');
        this.consentCheckbox = this.querySelector('#NewsletterOverlay-consent');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.emailField = this.querySelector('[data-field="email"]');
        this.errorElement = this.querySelector('[data-error="email"]');
        this.formHint = this.querySelector('.newsletter-overlay__form-hint');
      }

      bindEvents() {
        // Overlay open/close events from body-controller
        document.addEventListener('overlay:request-open', this.onRequestOpen.bind(this));
        document.addEventListener('overlay:request-close-all', this.onRequestCloseAll.bind(this));

        // Close buttons and backdrop
        this.querySelectorAll('[data-close-overlay]').forEach((el) => {
          el.addEventListener('click', this.close.bind(this));
        });

        // Listen for newsletter trigger clicks globally
        document.addEventListener('click', this.handleTriggerClick.bind(this));

        // Form validation on input
        if (this.emailInput) {
          this.emailInput.addEventListener('input', this.onInputChange.bind(this));
          this.emailInput.addEventListener('blur', this.validateEmail.bind(this));
        }

        if (this.consentCheckbox) {
          this.consentCheckbox.addEventListener('change', this.onInputChange.bind(this));
        }

        // Form submission
        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }

      handleTriggerClick(event) {
        const trigger = event.target.closest('[data-action="newsletter-trigger"]');
        if (!trigger) return;

        event.preventDefault();
        this.open();
      }

      onRequestOpen(event) {
        const { overlayId } = event.detail || {};
        if (overlayId === this.id) {
          this.open();
        }
      }

      onRequestCloseAll() {
        if (this.hasAttribute('open')) {
          this.close();
        }
      }

      open() {
        this.setAttribute('open', '');
        this.showView('form');

        // Dispatch event to body-controller
        document.dispatchEvent(
          new CustomEvent('overlay:open', {
            detail: { hash: this.dataset.overlayHash, overlayId: this.id },
          })
        );

        // Focus email input
        requestAnimationFrame(() => {
          if (this.emailInput) {
            this.emailInput.focus();
          }
        });
      }

      close() {
        this.removeAttribute('open');

        // Dispatch event to body-controller
        document.dispatchEvent(
          new CustomEvent('overlay:close', {
            detail: { overlayId: this.id },
          })
        );

        // Reset form after a brief delay (for animation)
        setTimeout(() => {
          this.resetForm();
        }, 300);
      }

      showView(viewName) {
        // Hide all views
        this.querySelectorAll('[data-view]').forEach((view) => {
          view.hidden = true;
        });

        // Show target view
        const targetView = this.querySelector(`[data-view="${viewName}"]`);
        if (targetView) {
          targetView.hidden = false;
        }
      }

      resetForm() {
        if (this.form) {
          this.form.reset();
        }
        this.clearError();
        this.updateButtonState();
        this.showView('form');
      }

      onInputChange() {
        // Clear error when user starts typing
        this.clearError();
        this.updateButtonState();
      }

      updateButtonState() {
        if (!this.submitButton || !this.emailInput || !this.consentCheckbox) return;

        const isEmailValid = this.isValidEmail(this.emailInput.value);
        const isConsentChecked = this.consentCheckbox.checked;

        this.submitButton.disabled = !(isEmailValid && isConsentChecked);
      }

      isValidEmail(email) {
        if (!email || !email.trim()) return false;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
      }

      validateEmail() {
        if (!this.emailInput) return true;

        const email = this.emailInput.value.trim();

        // Only show error if user has entered something invalid
        if (email && !this.isValidEmail(email)) {
          this.showError();
          return false;
        }

        this.clearError();
        return true;
      }

      showError() {
        if (this.emailField) {
          this.emailField.classList.add('newsletter-overlay__field--error');
        }
        if (this.errorElement) {
          const errorMsg =
            this.errorElement.dataset.errorMessage || 'Invalid email address. Please try again.';
          this.errorElement.textContent = errorMsg;
        }
        if (this.formHint) {
          this.formHint.hidden = true;
        }
      }

      clearError() {
        if (this.emailField) {
          this.emailField.classList.remove('newsletter-overlay__field--error');
        }
        if (this.errorElement) {
          this.errorElement.textContent = '';
        }
        if (this.formHint) {
          this.formHint.hidden = false;
        }
      }

      async handleSubmit(event) {
        event.preventDefault();

        // Final validation
        if (!this.validateEmail()) {
          return;
        }

        if (!this.consentCheckbox?.checked) {
          return;
        }

        const email = this.emailInput.value.trim();

        // Show loading state
        this.submitButton.classList.add('is-loading');
        this.submitButton.disabled = true;

        try {
          await this.subscribeToKlaviyo(email);
          this.showView('success');
        } catch (error) {
          console.error('Newsletter subscription error:', error);
          this.showError();
        } finally {
          this.submitButton.classList.remove('is-loading');
          // Keep button disabled after error, user needs to fix input
        }
      }

      async subscribeToKlaviyo(email) {
        const publicKey = this.dataset.klaviyoPublicKey;
        const listId = this.dataset.klaviyoListId;

        if (listId && publicKey) {
          const response = await fetch(
            `${KLAVIYO_SUBSCRIPTIONS_URL}?company_id=${encodeURIComponent(publicKey)}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/vnd.api+json',
                revision: KLAVIYO_API_REVISION,
              },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: {
                          email: email,
                          subscriptions: {
                            email: { marketing: { consent: 'SUBSCRIBED' } },
                          },
                          properties: {
                            region: this.dataset.region,
                            language: this.dataset.language,
                          },
                        },
                      },
                    },
                  },
                  relationships: {
                    list: {
                      data: { type: 'list', id: listId },
                    },
                  },
                },
              }),
            }
          );

          if (!response.ok) {
            throw new Error(`Klaviyo subscription failed with status ${response.status}`);
          }
        } else {
          // Graceful fallback - simulate success for testing
          console.warn('Klaviyo list ID or public key not configured - simulating success');
          return new Promise((resolve) => {
            setTimeout(resolve, 500);
          });
        }
      }
    }
  );
}
